import { Box, Typography } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import { TripProject, TripProjectFieldKey } from '../../../resources/dossier/types';
import { AllTranslationKeys } from '../../../shared/intl/types';

/**
 * Mapping of trip project field keys to their corresponding i18n translation keys.
 **/
const QA_FIELDS: { key: TripProjectFieldKey; translationId: AllTranslationKeys }[] = [
    {
        key: 'whyThisDestination',
        translationId: 'dossier.information.initial_request.why_this_destination',
    },
    {
        key: 'wishedExperiences',
        translationId: 'dossier.information.initial_request.wished_experiences',
    },
    {
        key: 'neededDestinationInformation',
        translationId: 'dossier.information.initial_request.needed_destination_information',
    },
    {
        key: 'otherDestinationsHesitation',
        translationId: 'dossier.information.initial_request.other_destinations_hesitation',
    },
    {
        key: 'previouslyTraveledWithAgency',
        translationId: 'dossier.information.initial_request.previously_traveled_with_agency',
    },
    {
        key: 'planToContactOtherAgencies',
        translationId: 'dossier.information.initial_request.plan_to_contact_other_agencies',
    },
    {
        key: 'decisionCriteria',
        translationId: 'dossier.information.initial_request.decision_criteria',
    },
    { key: 'project', translationId: 'dossier.information.initial_request.description' },
];

type ResolvedQAField = {
    field: NonNullable<TripProject[TripProjectFieldKey]>;
    translationId: AllTranslationKeys;
};

/** Filters QA_FIELDS to only those present in the given trip project. */
export const getDefinedQAFields = (tripProject: TripProject): ResolvedQAField[] => {
    const result: ResolvedQAField[] = [];

    for (const { key, translationId } of QA_FIELDS) {
        const field = tripProject[key];
        if (field) {
            result.push({ field, translationId });
        }
    }

    return result;
};

type TripProjectQuestionsAnswersProps = {
    tripProject: TripProject;
};

export function TripProjectQuestionsAnswers({
    tripProject,
}: Readonly<TripProjectQuestionsAnswersProps>) {
    const { formatMessage } = useIntl();

    const fields = getDefinedQAFields(tripProject);

    if (fields.length === 0) {
        return null;
    }

    return (
        <Box
            component="dl"
            sx={{
                m: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mt: 2,
                pt: 2,
                pb: 1,
                borderTop: '1px solid',
                borderColor: 'border.main',
            }}
        >
            {fields.map(({ field, translationId }) => (
                <Box key={field.key} sx={{ m: 0 }}>
                    <Typography component="dt" variant="body2" color="text.secondary" mb={0.5}>
                        {formatMessage({ id: translationId })}
                    </Typography>
                    <Typography
                        component="dd"
                        variant="body2"
                        sx={{ m: 0, whiteSpace: 'pre-wrap' }}
                    >
                        {Array.isArray(field.valueTranslated)
                            ? field.valueTranslated.join(', ')
                            : field.valueTranslated}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}
